"use client";

/* 通用地址选择组件 */
// React
import React, { useState, useEffect, useCallback, useRef } from "react";
// 三方库/框架
import { useTranslation } from "@/i18n/client";
// 公共方法
import { 
  setAddressCookie, 
  getGeolocationByWebApi
} from "@/utils/addressUtils";
import { queryGlobalCountriesData } from "@/utils/countryUtils";
// 接口
import { Address, Country } from "@/apis/address/address.interface";
// 组件
import { Tabs, Tab, Divider } from "@mui/material";
import CountrySelect from "@/components/countrySelect";
import DeliveryList from "./components/DeliveryList";
import { DeliveryListRef } from "./components/DeliveryList";
import PickupList from "./components/PickupList";
// 资源
import AddressSelectCloseIcon from "@/assets/icons/address/AddressSelectCloseIcon.svg";
import AddressEditIcon from "@/assets/icons/address/AddressEditIcon.svg"
// import PickupIcon from "@/assets/icons/address/PickupIcon.svg"
// 枚举
import { PAGE_TYPE } from "@/constants/address";
// 样式
import styles from "./addressSelect.module.scss";

interface IProps {
  mode: "drawer" | "page";
  defaultShippingType?: "delivery" | "pickup"; // 默认选中的配送类型 不传默认delivery
  availableShippingTypes?: ("delivery" | "pickup")[]; // 可用的配送类型 不传默认展示delivery+pickup
  defaultPickupTypeTab?: "recommended" | "saved";
  availablePickupTypes?: ("recommended" | "saved")[];
  selectAddressId?: string | number; // 选中的地址id 如果未传shippingType则默认delivery
  canSwitchSite?: boolean; // 是否可以切换站点 默认可以切换
  canSwitchCountry?: boolean; // 是否可以切换国家 默认可以切换
  isFrigNeed?: boolean; // 是否需要冷藏冷冻
  showManage?: boolean; // 是否展示地址管理按钮
  pickupExtMap?: Record<string, any>; // 自提查询扩展参数
  setOpenDrawer?: (val: boolean) => void; // drawer模式下 支持传入抽屉开启/关闭方法
  onAddressApply?: (address: Address) => void; // 支持传入回调函数 当地址选择变化时触发 如果传入此属性 则地址选择组件不处理任何写逻辑 均由父组件处理
}

const AddressSelectContent: React.FC<IProps> = (({ 
  mode = "drawer", 
  defaultShippingType, 
  availableShippingTypes,
  defaultPickupTypeTab,
  availablePickupTypes,
  selectAddressId, 
  canSwitchSite = true, 
  canSwitchCountry = true,
  isFrigNeed,
  showManage = true,
  pickupExtMap,
  setOpenDrawer, 
  onAddressApply 
}) => {
  const { t } = useTranslation();

  const [availableTypes, setAvailableTypes] = useState<("delivery" | "pickup")[]>(["delivery"]);
  const [shippingTypeTab, setShippingTypeTab] = useState<"delivery" | "pickup">("delivery");

  const [currentCountryInfo, setCurrentCountryInfo] = useState<Country | null>();

  const deliveryListRef = useRef<DeliveryListRef>(null);

  useEffect(() => {
    // 进入地址组件 首先请求定位授权
    getGeolocationByWebApi();
  }, []);

  // 初始化配送类型
  useEffect(() => {    
    handleShippingTypeInit();
  }, [defaultShippingType, availableShippingTypes]);

  const handleShippingTypeInit = async () => {
    const { currentCountry } = await queryGlobalCountriesData();
    setCurrentCountryInfo(currentCountry);

    let types: ("delivery" | "pickup")[] = [];

    // 优先外部传入
    if (availableShippingTypes && availableShippingTypes.length > 0) {
      types = availableShippingTypes;
    } else {
      // 默认逻辑 + 国家规则判断
      const hasPickup = currentCountry?.hasPickup;
      
      const validTypes = ["delivery", "pickup"].filter(
        type => type === "delivery" || (type === "pickup" && hasPickup)
      ) as ("delivery" | "pickup")[];
      
      types = validTypes;
    }
    
    setAvailableTypes(types);
    
    const initialType = defaultShippingType && types.includes(defaultShippingType as "delivery" | "pickup") 
      ? defaultShippingType as "delivery" | "pickup"
      : types[0];
    setShippingTypeTab(initialType);
  };


  const handleShippingTypeTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setShippingTypeTab(newValue as "delivery" | "pickup");
  };

  const handleAddressManageJump = () => {
    try {
      window.open('/profile/my-address', "_self");
    } catch (error) {
      console.error('地址管理跳转失败:', error);
    }
  };

  const handleApply = async (addressInfo:  Address | null) => {
    if(addressInfo){
      if (onAddressApply) {
        onAddressApply(addressInfo);
      } else {
        setAddressCookie(addressInfo);
        location.reload();
      }
      setOpenDrawer?.(false);
    }
  };

  return (
    <div className={styles.addrSelectContent}>
      <div className={styles.headerBar}>
        <CountrySelect 
          className={styles.countrySelect}
          canSwitchCountry={canSwitchCountry}
          canSwitchSite={canSwitchSite}
          confirmNonCrossSite={true}
        />

        {mode === 'drawer' ? (
          <div onClick={() => setOpenDrawer?.(false)}>
            <AddressSelectCloseIcon />
          </div>
        ) : null}
      </div>

      {availableTypes.length == 1 && !showManage ? null : (
         <div className={styles.manageBar}>
          {availableTypes.length > 0 && (
            <>
              <Tabs
                className={styles.tabBar}
                value={shippingTypeTab}
                aria-label="address selection tabs"
                onChange={handleShippingTypeTabChange}
              >
                {availableTypes.includes("delivery") && (
                  <Tab 
                    value="delivery" 
                    label={<span className={styles.tabItem}>{t("common.delivery")}</span>}
                    style={{ ...(shippingTypeTab === "delivery" ? {color: '#1A1A1A'} : {})  }}
                  />
                )}
                {availableTypes.includes("pickup") && (
                  <Tab 
                    value="pickup" 
                    label={<span className={styles.tabItem}>{t("common.pickup")}</span>}
                    style={{ ...(shippingTypeTab === "pickup" ? {color: '#1A1A1A'} : {})  }}
                  />
                )}
              </Tabs>
              
              {showManage && (
                <div className={styles.manageBtn} onClick={handleAddressManageJump}>
                  <AddressEditIcon />
                  <span>{t("address.lib.manage")}</span>
                </div>
              )}
            </>
          )}
        </div>
      )}
      
      <Divider className={styles.divider} />

      {/* 列表 */}
      <div className={styles.addrListWrap}>
        {availableTypes.length > 0 && (
          <>
            <div style={{ display: shippingTypeTab === "delivery" ? "flex" : "none", height: "100%" }}>
              <DeliveryList 
                ref={deliveryListRef} 
                selectAddressId={selectAddressId} 
                onApply={handleApply}
              />
            </div>
            <div style={{ display: shippingTypeTab === "pickup" ? "flex" : "none", height: "100%" }}>
              <PickupList
                defaultPickupTypeTab={defaultPickupTypeTab}
                availablePickupTypes={availablePickupTypes}
                selectAddressId={selectAddressId}
                isFrigNeed={isFrigNeed}
                extMap={pickupExtMap}
                onApply={handleApply} 
              />
            </div>
          </>
        )}
      </div>
    </div>
  )
});

export default AddressSelectContent;
